import {
  defineNuxtPlugin,
  useRequestEvent,
  useRoute,
  useRuntimeConfig,
  useState,
} from '#imports'

import type { Market } from '@backmarket/http-api/src/api-models/Market'
import type { Locale } from '@backmarket/http-api/src/standards/Locale'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useSessionId } from '@backmarket/nuxt-module-identification/useSessionId'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useIsE2ETestingEnvironment } from '@backmarket/nuxt-module-qa/useIsE2ETestingEnvironment'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'

import {
  init as initAmplitude,
  setUserProperties as setAmplitudeUserProperties,
  setOptIn as setOptinAmplitude,
} from '../amplitude'
import {
  FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
  USER_GROUP_NAME,
  USER_GROUP_VALUES,
} from '../amplitude/constants'
import {
  init as initBraze,
  setBrazeNotInitializing,
  setOptIn as setOptInBraze,
} from '../braze'
import { useDataConsent } from '../composables/useDataConsent'
import { useFeatureFlags } from '../composables/useFeatureFlags'
import { ESSENTIAL_PURPOSE_ID } from '../constants'
import {
  addUserContext,
  setContext,
  setCrmId,
  setUserContext,
} from '../context'
import { VENDORS_ID } from '../didomi/config'
import { createDidomiScriptElement } from '../didomi/script'
import * as events from '../eventTrackers'
import { startCollectingEvents } from '../google-tag-manager'
import { initNoodle } from '../noodle'
import type { TrackingConfig, TrackingFunctions } from '../types'
import { getInitialContext } from '../utils/getInitialContext'
import { initWebVitals } from '../webVitals'

import { cleanLocalStorage } from './utils/cleanLocalStorage'
import { getWebVitalsRouteName } from './utils/getWebVitalsRouteName'

function initializeTracking() {
  const {
    public: { tracking: config },
  } = useRuntimeConfig()
  const { market, features } = useMarketplace()
  const visitorId = useVisitorId()
  const sessionId = useSessionId()
  const route = useRoute()
  const { isCookiesModalDisplayed, getNoticeVisible, getUserConsentForBraze } =
    useDataConsent()
  const isE2ETestingEnvironment = useIsE2ETestingEnvironment()

  const webVitalsRouteName = getWebVitalsRouteName(route, config.webVitals)

  // Forwarded Amplitude Device ID from mobile apps to webviews
  // This allows to continuously track users between mobile apps and embedded webviews
  const forwardedAmplitudeDeviceId = useState<string | undefined>(
    FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
  ).value

  initWebVitals({
    webVitalsRouteName,
    route,
    country: market.countryCode,
    webVitalsMonitoringEnabled: config.webVitals.isEnabled,
    logINPAttribution: config.webVitals.inpAttributionLoggingEnabled,
    logCLSAttribution: config.webVitals.clsAttributionLoggingEnabled,
    clsAttributionsDebugEnabled: config.webVitals.clsAttributionsDebugEnabled,
  })

  /* Context setting */
  const initialContext = getInitialContext(
    market,
    features,
    useExperiments(),
    useFeatureFlags(),
    config,
    visitorId,
    sessionId,
    { isDidomiEnabled: true },
  )
  setContext(initialContext)

  /* Noodle */
  const shouldInitNoodle = config.noodle.isEnabled && !isE2ETestingEnvironment

  if (shouldInitNoodle) {
    const noodleUserContext = {
      country: market.countryCode,
      locale: initialContext.locale as Locale,
      sessionId,
      visitorId,
    }

    const noodleConfig = {
      environment: config.environment,
      service: config.service,
      url: config.noodle.url,
      version: config.version,
    }

    initNoodle(noodleUserContext, noodleConfig)
  }

  if (!window.Didomi) return

  const userStatus = window.Didomi?.getCurrentUserStatus?.()

  /** Google Tag Manager
   *
   *  In countries where the cookies modal is displayed, events are only sent after the user has closed the cookies modal, regardless of their choice.
   *  In other countries, events are sent right away.
   *  GTM is a functionnal cookie, it cannot be disabled by the user. But we want to wait for the user to close the modal to start sending events.
   *
   * */
  if (config.googleTagManager.isEnabled) {
    if (isCookiesModalDisplayed.value) {
      window.Didomi.on?.('consent.changed', () => {
        startCollectingEvents()
      })
    } else {
      startCollectingEvents()
    }
  }

  /* Amplitude */
  const shouldInitAmplitude =
    !isE2ETestingEnvironment &&
    config.amplitude.isEnabled &&
    config.amplitude.enabledCountryCodes.includes(market.countryCode) &&
    userStatus.vendors[VENDORS_ID.amplitude]

  if (shouldInitAmplitude) {
    // Skip the default NOTLOGGED set when inside a mobile webview: the native
    // app has already set user_type via its own Amplitude SDK and overwriting
    // it here would corrupt the shared user profile.
    if (!forwardedAmplitudeDeviceId) {
      setAmplitudeUserProperties({
        [USER_GROUP_NAME]: USER_GROUP_VALUES.NOT_LOGGED,
      })
    }

    if (userStatus.vendors[VENDORS_ID.amplitude].enabled) {
      initAmplitude({ deviceId: forwardedAmplitudeDeviceId })
      setOptinAmplitude(true)
    }

    window.Didomi?.addVendorStatusListener?.(
      VENDORS_ID.amplitude,
      ({ enabled }) => {
        if (enabled) {
          initAmplitude({ deviceId: forwardedAmplitudeDeviceId })
        }
        setOptinAmplitude(enabled)
      },
    )
  }

  /* Braze */
  const shouldInitBraze =
    !isE2ETestingEnvironment &&
    config.braze.isEnabled &&
    userStatus.vendors[VENDORS_ID.braze]

  if (shouldInitBraze) {
    void initBraze({
      apiKey: config.braze.apiKey,
      enabled: userStatus.vendors[VENDORS_ID.braze]?.enabled,
      modalDisplayed: getNoticeVisible(),
    })

    void setOptInBraze(userStatus.vendors[VENDORS_ID.braze]?.enabled)

    window.Didomi?.on?.('consent.changed', () => {
      // If user declines Braze on first interaction, addVendorStatusListener never fires (no status change); resolve pending waiters with null.
      if (!getUserConsentForBraze()) {
        setBrazeNotInitializing()
      }
    })

    // Fires only when Braze vendor status actually changes (e.g. enabled: false → true).
    window.Didomi?.addVendorStatusListener?.(
      VENDORS_ID.braze,
      ({ enabled }) => {
        void setOptInBraze(enabled)
      },
    )
  } else {
    setBrazeNotInitializing()
  }
}

export function createTrackingPlugin() {
  const {
    public: { tracking: config },
  } = useRuntimeConfig()
  const { market } = useMarketplace()
  const { isDidomiEnabled, isCookiesModalDisplayed } = useDataConsent()

  if (!isDidomiEnabled) return {}

  // Retrieving the forwarded Amplitude Device ID from the request header and pass it to the client through useState
  // This allows to continuously track users between mobile apps and embedded webviews
  if (process.server || isTestEnv()) {
    const forwardedAmplitudeDeviceIdState = useState<string | undefined>(
      FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
    )
    const request = useRequestEvent()

    const amplitudeDeviceIdFromRequest = request?.headers?.get(
      'bm-amplitude-device-id',
    )

    if (amplitudeDeviceIdFromRequest) {
      forwardedAmplitudeDeviceIdState.value = amplitudeDeviceIdFromRequest
    }
  }

  if (process.client) {
    const route = useRoute()
    const logger = useLogger()
    const $httpFetch = useHttpFetch()
    // FIXME COM-3790 - centralize the webview detection logic in a shared utility function to be reused across modules and scopes
    const isWebview = window.navigator.userAgent.includes(
      'bm-mobile-app-webview',
    )

    // delay didomi modal displaying for PLPs
    if (route.name === 'landing_page' || route.name === 'universe_page') {
      displayModalOnFirstInteraction({ config, market })
    }

    if (config.didomi.isCleanupEnabled && !isWebview) {
      // Skip the first consent.changed event when the modal was shown on load
      let skipNextConsentChange = isCookiesModalDisplayed.value
      window.didomiEventListeners = window.didomiEventListeners || []
      window.didomiEventListeners.push({
        event: 'consent.changed',
        listener: async () => {
          if (skipNextConsentChange) {
            skipNextConsentChange = false

            return
          }

          if (areVendorsOrPurposesDisabled()) {
            try {
              await $httpFetch('/delete-cookies', {
                method: 'DELETE',
                baseURL: window.location.origin,
              })

              cleanLocalStorage()

              // Ensure third-party scripts already in memory stop running after cookies are cleared
              window.location.reload()
            } catch (error) {
              logger.error('Consent withdrawal cleanup failure', {
                error: error as Error,
                owners: ['bot-squad-content-front'],
              })
            }
          }
        },
      })
    }

    window.didomiOnReady = window.didomiOnReady || []
    window.didomiOnReady.push(() => {
      initializeTracking()
    })
  }

  return {
    provide: {
      tracking: {
        ...events,
        addUserContext,
        setContext,
        setUserContext,
        setCrmId,
      } satisfies Pick<
        TrackingFunctions,
        'setContext' | 'setUserContext' | 'setCrmId' | 'addUserContext'
      >,
    },
  }
}

export default defineNuxtPlugin({
  name: 'didomiTracking',
  dependsOn: ['logger'],
  setup: createTrackingPlugin,
})

/**
 * Check if at least one non-functional vendor or purpose is disabled.
 *
 * Since it's not possible to know which cookies are set by which vendors/purposes, we consider that if the consent for at least one non-essential vendor or purpose is withdrawn,
 * then all cookies set by those vendors/purposes can be deleted.
 *
 * @returns true if at least one non-essential vendor or purpose is disabled, false otherwise
 */
function areVendorsOrPurposesDisabled() {
  if (!window.Didomi) return false

  const data = window.Didomi.getCurrentUserStatus()

  const optionalVendorsList = window.Didomi.getRequiredVendors()
    .filter((vendor) => !vendor.purposeIds.includes(ESSENTIAL_PURPOSE_ID))
    .map((vendor) => vendor.didomiId)

  const userHasSomeVendorsDisabled = Object.values(data.vendors).some(
    (vendor) => !vendor.enabled && optionalVendorsList.includes(vendor.id),
  )

  const optionalPurposesList = window.Didomi.getRequiredPurposeIds().filter(
    (purpose) => purpose !== ESSENTIAL_PURPOSE_ID,
  )

  const userHasSomePurposesDisabled = Object.values(data.purposes).some(
    (purpose) => !purpose.enabled && optionalPurposesList.includes(purpose.id),
  )

  return userHasSomeVendorsDisabled || userHasSomePurposesDisabled
}

function displayModalOnFirstInteraction({
  config,
  market,
}: {
  config: TrackingConfig
  market: Market
}) {
  let hasInteracted = false

  function addDidomiScriptOnFirstInteraction() {
    if (hasInteracted) return
    const didomiScriptElement = createDidomiScriptElement({
      apiKey: config.didomi.apiKey,
      noticeId: config.didomi.noticeId[market.countryCode],
      countryCode: market.countryCode,
    })
    document.head.appendChild(didomiScriptElement)
    hasInteracted = true
  }

  document.addEventListener('scroll', addDidomiScriptOnFirstInteraction, {
    once: true,
  })

  document.addEventListener('click', addDidomiScriptOnFirstInteraction, {
    once: true,
  })

  document.addEventListener('keyup', addDidomiScriptOnFirstInteraction, {
    once: true,
  })

  document.addEventListener('touchend', addDidomiScriptOnFirstInteraction, {
    once: true,
  })

  document.addEventListener('touchcancel', addDidomiScriptOnFirstInteraction, {
    once: true,
  })
}
